/**
 * MovementModal — Crear movimiento manual (ajuste, entrada, salida).
 * Producto buscado en el servidor (ProductAutocomplete), NumericInput para cantidad/costo.
 */
import { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Loader2 } from 'lucide-react'
import { inputCls, labelCls, errorCls } from '@/lib/formStyles'
import { ProductAutocomplete } from '@/modules/sales/components/ProductAutocomplete'
import { NumericInput } from '@/components/NumericInput'
import { useTenantLocale } from '@/lib/locale'
import { useWarehouses } from '../hooks/useInventory'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { Modal } from '@/components/ds/Modal'

interface Props {
  open: boolean
  onClose: () => void
  onSubmit: (data: any) => void
  isPending: boolean
}

export default function MovementModal({ open, onClose, onSubmit, isPending }: Props) {
  const locale = useTenantLocale()
  const { register, handleSubmit, reset, watch, control, setValue, formState: { errors } } = useForm()
  const { data: whData } = useWarehouses({ active: 'true', page_size: 9999 })

  const [productDisplay, setProductDisplay] = useState('')

  const movementType = watch('movement_type')
  const showCost = ['inbound', 'adjustment_pos'].includes(movementType)

  useEffect(() => {
    if (open) {
      reset({ movement_type: 'inbound', quantity: '', unit_cost: '', notes: '', warehouse_id: '', product_id: '' })
      setProductDisplay('')
    }
  }, [open, reset])

  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [open, onClose])

  if (!open) return null

  // La salida y el ajuste negativo salen al costo promedio: el campo de costo no
  // se muestra, pero react-hook-form conserva su valor y lo mandaba como "" —
  // el backend lo rechazaba y ninguna salida manual se registraba (H-M08-01).
  const enviar = (data: any) => {
    const limpio = { ...data }
    if (!showCost || limpio.unit_cost === '' || limpio.unit_cost == null) delete limpio.unit_cost
    onSubmit(limpio)
  }

  return (
    <Modal open onClose={onClose} title="Nuevo movimiento" size="lg">
      <form onSubmit={handleSubmit(enviar)} className="space-y-3">
        <div>
          <label htmlFor="movementmodal-warehouse-id" className={labelCls}>Bodega <span className="text-danger-solid">*</span></label>
          <select id="movementmodal-warehouse-id" {...register('warehouse_id', { required: 'Seleccione una bodega.' })} className={inputCls}>
            <option value="">Seleccionar...</option>
            {(whData?.warehouses || []).map(w => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
          {errors.warehouse_id && <p className={errorCls}>{String(errors.warehouse_id.message)}</p>}
        </div>

        <SelectField
          dense
          label="Tipo de movimiento"
          required
          selectProps={register('movement_type', { required: 'Seleccione un tipo.' })}
        >
          <option value="inbound">Entrada</option>
          <option value="outbound">Salida</option>
          <option value="adjustment_pos">Ajuste Positivo</option>
          <option value="adjustment_neg">Ajuste Negativo</option>
        </SelectField>

        <div>
          <label htmlFor="movementmodal-producto" className={labelCls}>Producto <span className="text-danger-solid">*</span></label>
          <ProductAutocomplete id="movementmodal-producto" field="name" type="product"
            value={productDisplay}
            onChange={(texto) => {
              // Escribir de nuevo invalida la selección anterior.
              setProductDisplay(texto)
              setValue('product_id', '')
            }}
            onSelectProduct={(prod) => {
              setValue('product_id', prod.id, { shouldValidate: true })
              setProductDisplay(`${prod.name} (${prod.sku})`)
            }}
            placeholder="Buscar por nombre o SKU..."
            className={inputCls}
          />
          <input type="hidden" {...register('product_id', { required: 'Seleccione un producto.' })} />
          {errors.product_id && <p className={errorCls}>{String(errors.product_id.message)}</p>}
        </div>

        <div className={`grid ${showCost ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
          <div>
            <label htmlFor="movementmodal-quantity" className={labelCls}>Cantidad <span className="text-danger-solid">*</span></label>
            <Controller name="quantity" control={control} rules={{ required: 'Ingrese la cantidad.' }}
              render={({ field }) => (
                <NumericInput id="movementmodal-quantity"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  locale={locale}
                  decimals={4}
                  placeholder="0"
                  min={0}
                  className={inputCls}
                />
              )}
            />
            {errors.quantity && <p className={errorCls}>{String(errors.quantity.message)}</p>}
          </div>
          {showCost && (
            <div>
              <label htmlFor="movementmodal-unit-cost" className={labelCls}>Costo unitario</label>
              <Controller name="unit_cost" control={control}
                render={({ field }) => (
                  <NumericInput id="movementmodal-unit-cost"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    locale={locale}
                    decimals={2}
                    placeholder="0"
                    min={0}
                    className={inputCls}
                  />
                )}
              />
              <p className="text-xs text-content-subtle mt-1">Se usa para recalcular el costo promedio ponderado.</p>
            </div>
          )}
        </div>

        <TextField
          dense
          label="Notas"
          multiline
          rows={2}
          placeholder="Observaciones..."
          inputProps={register('notes')}
        />

        <div className="flex justify-end gap-3 pt-4 border-t border-divider">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft">Cancelar</button>
          <button type="submit" disabled={isPending} className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
            {isPending && <Loader2 size={14} className="animate-spin" />}
            Registrar movimiento
          </button>
        </div>
      </form>
    </Modal>
  )
}
